/**
 * Against Medical Advice (AMA) form — PDF component and field definitions.
 *
 * Almost entirely auto-filled from context. Manual fields: visit date,
 * pet colour, markings, and attending vet.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { formatAge } from '@/frontend/features/printing/documents/blocks/format-utils'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface AmaFormFields {
  visitDate: string
  colour: string
  markings: string
  vetName: string
}

export const defaultAmaFormFields: AmaFormFields = {
  visitDate: '',
  colour: '',
  markings: '',
  vetName: '',
}

/** Format the patient's neutered/spayed status for display. */
export function formatHormonalStatus(isNeutered: boolean | null): string {
  if (isNeutered === true) return 'Yes'
  if (isNeutered === false) return 'No'
  return 'Unknown'
}

export { formatAge } from '@/frontend/features/printing/documents/blocks/format-utils'

export function amaConsentText(ownerFirst: string, ownerLast: string): string {
  return `I, ${ownerFirst} ${ownerLast}, understand that I am acting against medical advice and recommendations by the doctor.`
}

export function amaRiskText(patientName: string): string {
  return `Furthermore, I understand the attending veterinarian caring for my pet, ${patientName}, has advised me of the risks to the health of my pet going against medical advice. I also understand that by choosing to do this, my pet's health may be negatively impacted and may result in serious health conditions and/or diseases, potentially resulting in death.`
}

export const AMA_LIABILITY_TEXT =
  'I will not hold any employee of the clinic, temporary or otherwise, responsible for legal or medically negative outcomes and I will not hold the clinic liable for any damages associated with the loss or debilitation of my pet or legal ramifications.'

export const AMA_UNDERSTANDING_TEXT =
  'I am signing this with a full understanding of the medical and legal relevance of administering treatments or carrying out procedures against medical advice. I have had the opportunity to ask any questions and to have everything explained to me so I understand what I am declining.'

const styles = StyleSheet.create({
  ...baseStyles,
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
  },
  tableCell: {
    flex: 1,
    paddingVertical: mm(2),
    paddingHorizontal: mm(3),
    fontSize: 10,
  },
  tableCellLabel: {
    flex: 1,
    paddingVertical: mm(2),
    paddingHorizontal: mm(3),
    fontSize: 10,
    color: '#666',
  },
  tableHeader: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    padding: mm(3),
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
  },
})

export function AmaFormPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: AmaFormFields
}) {
  const { patient, owner, clinic } = context
  const age = patient.dateOfBirth ? formatAge(patient.dateOfBirth) : ''

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <OwnerDetails owner={owner} />

        {/* Patient details table */}
        <View style={{ ...styles.sectionGap, borderWidth: 0.5, borderColor: '#ccc' }}>
          <Text style={styles.tableHeader}>{patient.name}</Text>

          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Species</Text>
            <Text style={styles.tableCell}>{patient.species}</Text>
            <Text style={styles.tableCellLabel}>Visit Date</Text>
            <Text style={styles.tableCell}>{fields.visitDate || ' '}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Sex</Text>
            <Text style={styles.tableCell}>{patient.sex}</Text>
            <Text style={styles.tableCellLabel}>Weight</Text>
            <Text style={styles.tableCell}>{context.weight || ' '}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Age</Text>
            <Text style={styles.tableCell}>{age || ' '}</Text>
            <Text style={styles.tableCellLabel}>Microchip Number</Text>
            <Text style={styles.tableCell}>{patient.microchipNumber || ' '}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Breed</Text>
            <Text style={styles.tableCell}>{patient.breed || ' '}</Text>
            <Text style={styles.tableCellLabel}>Spayed/Neutered</Text>
            <Text style={styles.tableCell}>{formatHormonalStatus(patient.isNeutered)}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCellLabel}>Colour</Text>
            <Text style={styles.tableCell}>{fields.colour || ' '}</Text>
            <Text style={styles.tableCellLabel}>Markings</Text>
            <Text style={styles.tableCell}>{fields.markings || ' '}</Text>
          </View>
        </View>

        {/* AMA paragraphs */}
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{amaConsentText(owner.firstName, owner.lastName)}</Text>
        </View>
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{amaRiskText(patient.name)}</Text>
        </View>
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{AMA_LIABILITY_TEXT}</Text>
        </View>
        <View style={styles.sectionGap}>
          <Text style={styles.text}>{AMA_UNDERSTANDING_TEXT}</Text>
        </View>

        <OwnerSignature owner={owner} label="Pet parent" />
        <VetSignoff vetName={fields.vetName} label="Licensed veterinarian" showSignature showDate />
      </Page>
    </Document>
  )
}
